import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Paperclip } from 'lucide-react';

const IP_BLOCKS = ['none', '/30', '/29', '/28', '/27', '/26'];
const BILLING = [
  { v: 'monthly', l: 'Monthly' }, { v: 'quarterly', l: 'Quarterly' },
  { v: 'half_yearly', l: 'Half-Yearly' }, { v: 'yearly', l: 'Yearly' },
];
const emptySite = { label: '', address: '', city: '', pincode: '', contact_person: '', contact_phone: '' };

export const blankOrder = () => ({
  customer_id: '', service_type: 'ILL', bandwidth_mbps: '', sites: [{ ...emptySite }],
  ip_block: 'none', contract_term_months: 12, otc: '', mrc: '', billing_cycle: 'monthly',
  expected_delivery_date: '', notes: '',
});

const siteLabel = (type, i) => (type === 'MPLS' ? (i === 0 ? 'A-End' : i === 1 ? 'B-End' : `Site ${i + 1}`) : 'Installation Site');

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wide text-slate-500">{label}</Label>{children}</div>
);

const SiteRow = ({ site, idx, type, onChange, onRemove, canRemove }) => (
  <div className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50/50" data-testid={`so-site-${idx}`}>
    <div className="flex items-center justify-between">
      <span className="text-sm font-semibold text-slate-800">{siteLabel(type, idx)}</span>
      {canRemove && <Button type="button" variant="ghost" size="sm" onClick={onRemove} data-testid={`so-site-remove-${idx}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>}
    </div>
    <Input data-testid={`so-site-address-${idx}`} placeholder="Full address *" value={site.address} onChange={e => onChange('address', e.target.value)} />
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      <Input data-testid={`so-site-city-${idx}`} placeholder="City" value={site.city} onChange={e => onChange('city', e.target.value)} />
      <Input data-testid={`so-site-pincode-${idx}`} placeholder="Pincode" value={site.pincode} onChange={e => onChange('pincode', e.target.value)} />
      <Input data-testid={`so-site-contact-${idx}`} placeholder="Site contact" value={site.contact_person} onChange={e => onChange('contact_person', e.target.value)} />
      <Input data-testid={`so-site-phone-${idx}`} placeholder="Contact phone" value={site.contact_phone} onChange={e => onChange('contact_phone', e.target.value)} />
    </div>
  </div>
);

export const SalesOrderForm = ({ initial, customers, onSubmit, saving }) => {
  const [f, setF] = useState(initial);
  const [files, setFiles] = useState([]);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const setSite = (i, k, v) => set('sites', f.sites.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const setType = (t) => setF(prev => ({ ...prev, service_type: t, sites: t === 'MPLS' && prev.sites.length < 2 ? [...prev.sites, { ...emptySite }] : prev.sites }));
  const minSites = f.service_type === 'MPLS' ? 2 : 1;

  const submit = (submitForApproval) => (e) => {
    e.preventDefault();
    onSubmit({
      ...f,
      bandwidth_mbps: parseFloat(f.bandwidth_mbps), otc: parseFloat(f.otc || 0), mrc: parseFloat(f.mrc || 0),
      contract_term_months: parseInt(f.contract_term_months, 10),
      sites: f.sites.map((s, i) => ({ ...s, label: siteLabel(f.service_type, i) })),
      expected_delivery_date: f.expected_delivery_date || null,
      submit_for_approval: submitForApproval,
    }, files);
  };

  return (
    <form onSubmit={submit(true)} className="space-y-5" data-testid="sales-order-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Customer *">
          <Select value={f.customer_id} onValueChange={v => set('customer_id', v)}>
            <SelectTrigger data-testid="so-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
            <SelectContent>
              {customers.map(c => <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name} ({c.customer_code})</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Service Type *">
          <div className="flex gap-2">
            {['ILL', 'MPLS'].map(t => (
              <button key={t} type="button" data-testid={`so-type-${t}`} onClick={() => setType(t)}
                className={`flex-1 h-9 rounded-md border text-xs whitespace-nowrap font-medium transition-colors duration-150 ${f.service_type === t ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-400'}`}>
                {t === 'ILL' ? 'ILL (Leased Line)' : 'MPLS'}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Bandwidth (Mbps) *">
          <Input data-testid="so-bandwidth" type="number" min="1" required value={f.bandwidth_mbps} onChange={e => set('bandwidth_mbps', e.target.value)} />
        </Field>
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-wide text-slate-500">Sites / Locations *</Label>
        {f.sites.map((s, i) => (
          <SiteRow key={i} site={s} idx={i} type={f.service_type} onChange={(k, v) => setSite(i, k, v)}
            onRemove={() => set('sites', f.sites.filter((_, j) => j !== i))} canRemove={f.sites.length > minSites} />
        ))}
        {f.service_type === 'MPLS' && (
          <Button type="button" variant="outline" size="sm" data-testid="so-add-site" onClick={() => set('sites', [...f.sites, { ...emptySite }])}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Add Site
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Field label="IP Block">
          <Select value={f.ip_block} onValueChange={v => set('ip_block', v)}>
            <SelectTrigger data-testid="so-ip-block"><SelectValue /></SelectTrigger>
            <SelectContent>{IP_BLOCKS.map(b => <SelectItem key={b} value={b}>{b === 'none' ? 'Not required' : b}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Contract Term (months) *">
          <Input data-testid="so-term" type="number" min="1" required value={f.contract_term_months} onChange={e => set('contract_term_months', e.target.value)} />
        </Field>
        <Field label="OTC (₹) *">
          <Input data-testid="so-otc" type="number" min="0" required value={f.otc} onChange={e => set('otc', e.target.value)} />
        </Field>
        <Field label="MRC (₹) *">
          <Input data-testid="so-mrc" type="number" min="0" required value={f.mrc} onChange={e => set('mrc', e.target.value)} />
        </Field>
        <Field label="Billing Cycle">
          <Select value={f.billing_cycle} onValueChange={v => set('billing_cycle', v)}>
            <SelectTrigger data-testid="so-billing-cycle"><SelectValue /></SelectTrigger>
            <SelectContent>{BILLING.map(b => <SelectItem key={b.v} value={b.v}>{b.l}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Expected Delivery">
          <Input data-testid="so-delivery-date" type="date" value={f.expected_delivery_date || ''} onChange={e => set('expected_delivery_date', e.target.value)} />
        </Field>
        <div className="col-span-2">
          <Field label="Attachments (PO, CAF, KYC...)">
            <label className="flex items-center gap-2 h-9 px-3 border border-dashed border-slate-300 rounded-md text-sm text-slate-600 cursor-pointer hover:border-slate-500">
              <Paperclip className="h-4 w-4" />{files.length ? `${files.length} file(s) selected` : 'Choose files'}
              <input data-testid="so-attachments-input" type="file" multiple className="hidden" onChange={e => setFiles(Array.from(e.target.files || []))} />
            </label>
          </Field>
        </div>
      </div>

      <Field label="Notes">
        <Textarea data-testid="so-notes" rows={3} value={f.notes || ''} onChange={e => set('notes', e.target.value)} />
      </Field>

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
        <Button type="button" variant="outline" disabled={saving} onClick={submit(false)} data-testid="so-save-draft">Save as Draft</Button>
        <Button type="submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="so-submit-approval">
          {saving ? 'Saving...' : 'Submit for Approval'}
        </Button>
      </div>
    </form>
  );
};
